import AppModal from "../../../components/modal/AppModal";
import style from "../../../assets/css/appModal.module.scss";
import { updateCronJobSchedule } from "../../../services/cronJobService";
import { useState } from "react";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import {
  TextField,
  styled,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";

const StyledDateTimePicker = styled(DateTimePicker)({
  ".MuiInput-underline:before": {
    borderBottom: "1px solid #6F719B !important",
  },
  ".MuiInput-underline:after": {
    borderBottom: "2px solid #F9DA87",
  },
});

const DateTextField = styled(TextField)({
  width: "100%",
  marginTop: "16px",
  "& .MuiInput-underline:after": {
    borderBottom: "none",
  },
  "& .MuiSvgIcon-root": {
    color: "#6F719B",
  },
});

const CronJobScheduleModal = ({
  jobName,
  currentState,
  getJobsList,
  handleClose,
}) => {
  const [actionType, setActionType] = useState(null);
  const [scheduledDateTime, setScheduledDateTime] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState("");
  const [updatedState, setUpdatedState] = useState("");

  // Check if status change should be available
  const canChangeStatus =
    currentState === "NORMAL" ||
    currentState === "ERROR" ||
    currentState === "PAUSED";

  const handleChangeStatus = () => {
    if (!updatedState) {
      setResponse({
        error: true,
        message: "Please select a status to change",
      });
      return;
    }

    setIsLoading(true);
    updateCronJobSchedule(jobName, null, false, updatedState)
      .then((data) => {
        const { error, success, message } = data || {};
        setIsLoading(false);
        if (error) {
          setResponse({
            error,
            message: error.message || "Something went wrong",
          });
          return;
        }
        setResponse({
          success,
          message: message || "Job status updated successfully!",
        });
        getJobsList();
      })
      .catch((error) => {
        setIsLoading(false);
        setResponse({
          error: true,
          message: error.data?.error?.message || "Something went wrong",
        });
      });
  };

  const handleRunNow = () => {
    setIsLoading(true);
    updateCronJobSchedule(jobName, null, true, updatedState || undefined)
      .then((data) => {
        const { error, success, message } = data || {};
        setIsLoading(false);
        if (error) {
          setResponse({
            error,
            message: error.message || "Something went wrong",
          });
          return;
        }
        setResponse({
          success,
          message: message || "Job scheduled to run now successfully!",
        });
        getJobsList();
      })
      .catch((error) => {
        setIsLoading(false);
        setResponse({
          error: true,
          message: error.data?.error?.message || "Something went wrong",
        });
      });
  };

  const isValidFutureDate = (date) => {
    if (!date) return false;
    const selectedMoment = moment(date);
    const now = moment();
    return selectedMoment.isValid() && selectedMoment.isAfter(now);
  };

  const handleScheduleForLater = () => {
    if (!scheduledDateTime) {
      setResponse({
        error: true,
        message: "Please select a date and time",
      });
      return;
    }

    if (!isValidFutureDate(scheduledDateTime)) {
      setResponse({
        error: true,
        message: "Please select a valid future date and time",
      });
      return;
    }

    setIsLoading(true);
    // Convert selected date/time to timestamp (milliseconds)
    const timestamp = moment(scheduledDateTime).valueOf();

    updateCronJobSchedule(jobName, timestamp, false, updatedState || undefined)
      .then((data) => {
        const { error, success, message } = data || {};
        setIsLoading(false);
        if (error) {
          setResponse({
            error,
            message: error.message || "Something went wrong",
          });
          return;
        }
        setResponse({
          success,
          message: message || "Job scheduled successfully!",
        });
        getJobsList();
      })
      .catch((error) => {
        setIsLoading(false);
        setResponse({
          error: true,
          message: error.data?.error?.message || "Something went wrong",
        });
      });
  };

  const handleCloseModal = () => {
    setActionType(null);
    setScheduledDateTime(null);
    setUpdatedState("");
    setResponse("");
    handleClose();
  };

  return (
    <div>
      <AppModal handleClose={handleCloseModal}>
        <div className={style.container}>
          <div className={style.bodyBox}>
            <div className={style.closeIcon} onClick={handleCloseModal}>
              <span className="fa fa-close"></span>
            </div>

            <div className={style.header}>
              {!response
                ? `Schedule Job: ${jobName}`
                : response.error
                ? "Oops!"
                : "Success!"}
            </div>

            {!response ? (
              <>
                {!actionType ? (
                  <>
                    <div className={style.inputContainer}>
                      <div className={style.label}>Choose an action:</div>
                    </div>

                    <div className={style.cronJobFooter}>
                      <div className={style.btn1}>
                        <button
                          className="btn btn-primary text-nowrap py-2 px-5"
                          onClick={() => setActionType("runNow")}
                          disabled={isLoading}
                        >
                          RUN NOW
                        </button>
                      </div>

                      <div className={style.btn1}>
                        <button
                          className="btn btn-primary py-2 px-5"
                          onClick={() => setActionType("schedule")}
                          disabled={isLoading}
                        >
                          SCHEDULE FOR LATER
                        </button>
                      </div>

                      {canChangeStatus && (
                        <div className={style.btn1}>
                          <button
                            className="btn btn-primary py-2 px-5"
                            onClick={() => setActionType("changeStatus")}
                            disabled={isLoading}
                          >
                            CHANGE STATUS
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                ) : actionType === "runNow" ? (
                  <>
                    <div className={style.inputContainer}>
                      <div className={style.label}>
                        Are you sure you want to run this job now?
                      </div>
                      {canChangeStatus && (
                        <div style={{ marginTop: "16px" }}>
                          <FormControl
                            variant="standard"
                            sx={{ minWidth: 200, width: "100%" }}
                          >
                            <InputLabel id="status-select-label-runnow">
                              Change Status (Optional)
                            </InputLabel>
                            <Select
                              labelId="status-select-label-runnow"
                              id="status-select-runnow"
                              value={updatedState}
                              onChange={(e) => setUpdatedState(e.target.value)}
                              label="Change Status (Optional)"
                            >
                              <MenuItem value="">
                                <em>None</em>
                              </MenuItem>
                              {currentState === "NORMAL" && (
                                <MenuItem value="ERROR">ERROR</MenuItem>
                              )}
                              {currentState === "ERROR" && (
                                <MenuItem value="NORMAL">NORMAL</MenuItem>
                              )}
                              {currentState === "PAUSED" && (
                                <MenuItem value="NORMAL">NORMAL</MenuItem>
                              )}
                            </Select>
                          </FormControl>
                        </div>
                      )}
                    </div>

                    <div className={style.footer}>
                      <div className={style.btn1}>
                        <button
                          className="btn btn-primary text-nowrap py-2 px-5"
                          onClick={handleRunNow}
                          disabled={isLoading}
                        >
                          {isLoading ? "Processing..." : "Confirm Run Now"}
                        </button>
                      </div>

                      <div className={style.btn2}>
                        <button
                          className="btn btn-secondary py-2 px-5"
                          onClick={() => setActionType(null)}
                          disabled={isLoading}
                        >
                          Back
                        </button>
                      </div>
                    </div>
                  </>
                ) : actionType === "changeStatus" ? (
                  <>
                    <div className={style.inputContainer}>
                      <div className={style.label}>
                        Select the new status for this job:
                      </div>
                      <div style={{ marginTop: "16px" }}>
                        <FormControl
                          variant="standard"
                          sx={{ minWidth: 200, width: "100%" }}
                        >
                          <InputLabel id="status-select-label-changestatus">
                            Change Status
                          </InputLabel>
                          <Select
                            labelId="status-select-label-changestatus"
                            id="status-select-changestatus"
                            value={updatedState}
                            onChange={(e) => setUpdatedState(e.target.value)}
                            label="Change Status"
                          >
                            {currentState === "NORMAL" && (
                              <MenuItem value="ERROR">ERROR</MenuItem>
                            )}
                            {currentState === "ERROR" && (
                              <MenuItem value="NORMAL">NORMAL</MenuItem>
                            )}
                            {currentState === "PAUSED" && (
                              <MenuItem value="NORMAL">NORMAL</MenuItem>
                            )}
                          </Select>
                        </FormControl>
                      </div>
                    </div>

                    <div className={style.footer}>
                      <div className={style.btn1}>
                        <button
                          className="btn btn-primary text-nowrap py-2 px-5"
                          onClick={handleChangeStatus}
                          disabled={isLoading || !updatedState}
                        >
                          {isLoading ? "Updating..." : "Update Status"}
                        </button>
                      </div>

                      <div className={style.btn2}>
                        <button
                          className="btn btn-secondary py-2 px-5"
                          onClick={() => {
                            setActionType(null);
                            setUpdatedState("");
                          }}
                          disabled={isLoading}
                        >
                          Back
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className={style.inputContainer}>
                      <div className={style.label}>Select Date and Time:</div>
                      <div className={style.dateTimePickerContainer}>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                          <StyledDateTimePicker
                            inputFormat="DD/MM/YYYY HH:mm"
                            value={scheduledDateTime}
                            onChange={(newValue) =>
                              setScheduledDateTime(newValue)
                            }
                            minDateTime={moment()}
                            openTo="day"
                            views={["year", "month", "day", "hours", "minutes"]}
                            componentsProps={{
                              actionBar: {
                                actions: ["clear"],
                              },
                            }}
                            PopperProps={{
                              placement: "bottom-start",
                              style: {
                                zIndex: 1300,
                                position: "fixed",
                              },
                              disablePortal: false,
                              modifiers: [
                                {
                                  name: "preventOverflow",
                                  enabled: true,
                                  options: {
                                    altAxis: false,
                                    altBoundary: false,
                                    tether: false,
                                    rootBoundary: "viewport",
                                    padding: 8,
                                  },
                                },
                                {
                                  name: "flip",
                                  enabled: false,
                                },
                                {
                                  name: "offset",
                                  enabled: true,
                                  options: {
                                    offset: [0, 8],
                                  },
                                },
                                {
                                  name: "computeStyles",
                                  enabled: true,
                                  options: {
                                    adaptive: false,
                                    gpuAcceleration: false,
                                  },
                                },
                              ],
                            }}
                            renderInput={(params) => {
                              return (
                                <DateTextField
                                  fullWidth
                                  variant="standard"
                                  {...params}
                                  inputProps={{
                                    ...params.inputProps,
                                    placeholder: "Select date and time",
                                  }}
                                />
                              );
                            }}
                          />
                        </LocalizationProvider>
                      </div>
                      {canChangeStatus && (
                        <div style={{ marginTop: "16px" }}>
                          <FormControl
                            variant="standard"
                            sx={{ minWidth: 200, width: "100%" }}
                          >
                            <InputLabel id="status-select-label-schedule">
                              Change Status (Optional)
                            </InputLabel>
                            <Select
                              labelId="status-select-label-schedule"
                              id="status-select-schedule"
                              value={updatedState}
                              onChange={(e) => setUpdatedState(e.target.value)}
                              label="Change Status (Optional)"
                            >
                              <MenuItem value="">
                                <em>None</em>
                              </MenuItem>
                              {currentState === "NORMAL" && (
                                <MenuItem value="ERROR">ERROR</MenuItem>
                              )}
                              {currentState === "ERROR" && (
                                <MenuItem value="NORMAL">NORMAL</MenuItem>
                              )}
                              {currentState === "PAUSED" && (
                                <MenuItem value="NORMAL">NORMAL</MenuItem>
                              )}
                            </Select>
                          </FormControl>
                        </div>
                      )}
                    </div>

                    <div className={style.footer}>
                      <div className={style.btn1}>
                        <button
                          className="btn btn-primary text-nowrap py-2 px-5"
                          onClick={handleScheduleForLater}
                          disabled={
                            isLoading ||
                            !scheduledDateTime ||
                            !isValidFutureDate(scheduledDateTime)
                          }
                        >
                          {isLoading ? "Scheduling..." : "Schedule Job"}
                        </button>
                      </div>

                      <div className={style.btn2}>
                        <button
                          className="btn btn-secondary py-2 px-5"
                          onClick={() => {
                            setActionType(null);
                            setScheduledDateTime(null);
                            setUpdatedState("");
                          }}
                          disabled={isLoading}
                        >
                          Back
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </>
            ) : (
              <>
                <div className={style.heading}>{response.message}</div>
                <div className={style.successBtn}>
                  <button
                    className="btn btn-primary text-nowrap py-2 px-5"
                    onClick={handleCloseModal}
                  >
                    Okay
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </AppModal>
    </div>
  );
};

export default CronJobScheduleModal;
